import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';
import { isOwner } from '../../utils/admin'; 

export default function AddItem() {
  const [formData, setFormData] = useState({ 
    title: '', 
    price: '', 
    category: 'General',
    condition: 'Used - Excellent', 
    brand: '', // 🚨 NEW: Hoisted to top-level for the new Feed filters
    description: '',
    location: '', 
    shippingAvailable: false, 
    shippingCost: '0' 
  });
  
  const [attributes, setAttributes] = useState({});
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // --- LIVE PAYOUT MATH ---
  const isAdmin = auth.currentUser ? isOwner(auth.currentUser.email) : false;
  const itemPrice = parseFloat(formData.price) || 0;
  const shipCost = formData.shippingAvailable ? (parseFloat(formData.shippingCost) || 0) : 0;
  const totalCharge = itemPrice + shipCost;
  // 🚨 FIXED: Updated from 5% to 7% to match the platform's marketing copy
  const platformFee = isAdmin ? 0 : (totalCharge * 0.07); 
  const estimatedPayout = totalCharge - platformFee;

  const categories = ['General', 'Electronics & Computers', 'Apparel & Accessories', 'Home & Furniture', 'Automotive', 'Toys & Hobbies'];

  const handleAttributeChange = (key, value) => {
    setAttributes(prev => ({ ...prev, [key]: value }));
  };

  const handleCategoryChange = (e) => {
    setFormData({ ...formData, category: e.target.value });
    setAttributes({}); 
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) return alert("Maximum 5 photos allowed.");
    setImages(files);
    previews.forEach(url => URL.revokeObjectURL(url));
    const filePreviews = files.map(file => URL.createObjectURL(file));
    setPreviews(filePreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (images.length === 0) return alert("Please upload at least one photo!");
    setIsSubmitting(true);

    try {
      const uploadPromises = images.map(async (file) => {
        const storageRef = ref(storage, `listings/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        return getDownloadURL(snapshot.ref);
      });

      const imageUrls = await Promise.all(uploadPromises);

      await addDoc(collection(db, 'listings'), {
        ...formData,
        price: parseFloat(formData.price),
        shippingCost: formData.shippingAvailable ? parseFloat(formData.shippingCost) : 0,
        attributes: attributes, 
        imageUrls: imageUrls, 
        mainImage: imageUrls[0], 
        sellerId: auth.currentUser.uid,
        sellerEmail: auth.currentUser.email,
        status: 'active',
        featured: false, 
        createdAt: serverTimestamp()
      });

      navigate('/profile');
    } catch (error) {
      console.error(error);
      alert('Upload failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', padding: '40px 20px', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', backgroundColor: 'white', borderRadius: '24px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
        
        {/* Elegant Header */}
        <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', padding: '40px', color: 'white', textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '10px' }}>📦</div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '800', letterSpacing: '-0.5px' }}>List New Gear</h2>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '15px' }}>Turn your unused hardware into cash instantly.</p>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '40px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div>
            <label style={labelStyle}>Item Title</label>
            <input type="text" placeholder="e.g., NVIDIA RTX 3080 Founders Edition" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            <div>
              <label style={labelStyle}>Category</label>
              <select value={formData.category} onChange={handleCategoryChange} required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}>
                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Condition</label>
              <select value={formData.condition} onChange={e => setFormData({...formData, condition: e.target.value})} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur}>
                <option value="New">Brand New</option>
                <option value="Used - Like New">Used - Like New</option>
                <option value="Used - Excellent">Used - Excellent</option>
                <option value="Used - Good">Used - Good</option>
                <option value="Used - Fair">Used - Fair</option>
              </select>
            </div>
            
            {/* 🚨 BRAND IS NOW A FIRST-CLASS CITIZEN FOR FILTERS */}
            <div>
              <label style={labelStyle}>Brand <span style={{ fontWeight: 'normal', color: '#94a3b8' }}>(Optional)</span></label>
              <input type="text" placeholder="e.g., Apple, Sony, Nike" value={formData.brand} onChange={e => setFormData({...formData, brand: e.target.value})} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
            </div>
          </div>

          {/* DYNAMIC ATTRIBUTES (For extra specs not covered by the main filters) */}
          {formData.category !== 'General' && (
            <div style={{ padding: '24px', backgroundColor: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 16px 0', color: '#0f172a', fontSize: '15px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Specific Details</h4>
              
              {formData.category === 'Apparel & Accessories' && (
                <div>
                  <label style={labelStyle}>Size</label>
                  <input type="text" placeholder="e.g., Medium, 10.5, 32x30" onChange={e => handleAttributeChange('Size', e.target.value)} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
                </div>
              )}

              {formData.category === 'Electronics & Computers' && (
                <div>
                  <label style={labelStyle}>Model Number</label>
                  <input type="text" placeholder="e.g., A2890, CUH-7000" onChange={e => handleAttributeChange('Model', e.target.value)} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
                </div>
              )}

              {formData.category === 'Home & Furniture' && (
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Dimensions (L x W x H)</label>
                    <input type="text" placeholder="e.g., 72 x 36 x 30 in" onChange={e => handleAttributeChange('Dimensions', e.target.value)} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Material</label>
                    <input type="text" placeholder="e.g., Oak, Leather" onChange={e => handleAttributeChange('Material', e.target.value)} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
                  </div>
                </div>
              )}

              {formData.category === 'Automotive' && (
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Make & Model</label>
                    <input type="text" placeholder="e.g., Honda Civic" onChange={e => handleAttributeChange('MakeModel', e.target.value)} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Year</label>
                    <input type="number" placeholder="e.g., 2018" onChange={e => handleAttributeChange('Year', e.target.value)} style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            <div>
              <label style={labelStyle}>Price ($)</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: '700' }}>$</span>
                <input type="number" step="0.01" placeholder="0.00" value={formData.price} onChange={e => setFormData({...formData, price: e.target.value})} required style={{...inputStyle, paddingLeft: '32px'}} onFocus={handleFocus} onBlur={handleBlur} />
              </div>
            </div>
            
            <div>
              <label style={labelStyle}>Item Location</label>
              <input type="text" placeholder="City, State (e.g., Chicago, IL)" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} required style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
            </div>
          </div>

          {/* SHIPPING SECTION */}
          <div style={{ padding: '24px', backgroundColor: formData.shippingAvailable ? '#eff6ff' : '#f8fafc', borderRadius: '16px', border: `1px solid ${formData.shippingAvailable ? '#bfdbfe' : '#e2e8f0'}`, transition: 'all 0.3s ease' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontWeight: '700', color: '#0f172a', fontSize: '16px' }}>
              <input 
                type="checkbox" 
                checked={formData.shippingAvailable} 
                onChange={e => setFormData({...formData, shippingAvailable: e.target.checked})} 
                style={{ width: '20px', height: '20px', accentColor: '#4f46e5', cursor: 'pointer' }}
              />
              I am willing to ship this item
            </label>

            {formData.shippingAvailable && (
              <div style={{ marginTop: '20px', animation: 'fadeIn 0.3s ease' }}>
                <label style={labelStyle}>Shipping Cost ($)</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: '700' }}>$</span>
                  <input type="number" step="0.01" placeholder="0.00" value={formData.shippingCost} onChange={e => setFormData({...formData, shippingCost: e.target.value})} required style={{...inputStyle, paddingLeft: '32px'}} onFocus={handleFocus} onBlur={handleBlur} />
                </div>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#64748b' }}>If you are offering free shipping, leave this as 0.00.</p>
              </div>
            )}
          </div>

          {/* TRANSPARENCY PAYOUT ESTIMATOR */}
          {totalCharge > 0 && (
            <div style={{ backgroundColor: '#f0fdf4', padding: '24px', borderRadius: '16px', border: '1px solid #bbf7d0' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#065f46', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '800' }}>🧾 Estimated Payout</h4>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', color: '#475569', marginBottom: '10px' }}>
                <span>Item Price + Shipping:</span>
                <span style={{ fontWeight: '600' }}>${totalCharge.toFixed(2)}</span>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', color: isAdmin ? '#059669' : '#ef4444', marginBottom: '16px' }}>
                <span>Platform Fee (7%):</span>
                <span style={{ fontWeight: '600' }}>{isAdmin ? 'Waived (Admin)' : `-$${platformFee.toFixed(2)}`}</span>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '20px', color: '#065f46', fontWeight: '900', paddingTop: '16px', borderTop: '2px dashed #a7f3d0' }}>
                <span>You Earn:</span>
                <span>${estimatedPayout.toFixed(2)}</span>
              </div>
            </div>
          )}

          <div>
            <label style={{ ...labelStyle, display: 'flex', justifyContent: 'space-between' }}>
              <span>Photos (Up to 5)</span>
              <span style={{ color: '#94a3b8', fontWeight: 'normal' }}>First photo is the cover</span>
            </label>
            <div style={{ border: '2px dashed #cbd5e1', padding: '30px', borderRadius: '16px', textAlign: 'center', backgroundColor: '#f8fafc', transition: 'border-color 0.2s', position: 'relative' }}
              onMouseOver={e => e.currentTarget.style.borderColor = '#4f46e5'}
              onMouseOut={e => e.currentTarget.style.borderColor = '#cbd5e1'}
            >
              <input type="file" accept="image/*" multiple onChange={handleImageChange} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
              <div style={{ fontSize: '32px', marginBottom: '10px' }}>📸</div>
              <p style={{ margin: 0, color: '#4f46e5', fontWeight: '700', fontSize: '15px' }}>Click to upload photos</p>
              <p style={{ margin: '5px 0 0 0', color: '#64748b', fontSize: '13px' }}>PNG, JPG, HEIC up to 10MB each</p>
            </div>
            
            {previews.length > 0 && (
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px' }}>
                {previews.map((url, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <img src={url} alt={`Preview ${i}`} style={{ width: '90px', height: '90px', objectFit: 'cover', borderRadius: '12px', border: i === 0 ? '3px solid #4f46e5' : '1px solid #cbd5e1' }} />
                    {i === 0 && <span style={{ position: 'absolute', bottom: '-8px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#4f46e5', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>COVER</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea placeholder="List technical specs, flaws, or bundle details to build trust with the buyer..." value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} required style={{ ...inputStyle, height: '140px', resize: 'vertical' }} onFocus={handleFocus} onBlur={handleBlur} />
          </div>

          <button type="submit" disabled={isSubmitting} style={{ padding: '18px', backgroundColor: isSubmitting ? '#94a3b8' : '#4f46e5', color: 'white', border: 'none', borderRadius: '12px', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontWeight: '800', fontSize: '18px', boxShadow: isSubmitting ? 'none' : '0 10px 15px -3px rgba(79, 70, 229, 0.3)', transition: 'all 0.2s', marginTop: '10px' }}
            onMouseOver={e => !isSubmitting && (e.currentTarget.style.transform = 'translateY(-2px)')}
            onMouseOut={e => !isSubmitting && (e.currentTarget.style.transform = 'translateY(0)')}
          >
            {isSubmitting ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <div style={{ width: '18px', height: '18px', border: '3px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                Publishing Listing...
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </span>
            ) : 'List Item for Sale'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Reusable Styles
const labelStyle = { 
  display: 'block', 
  marginBottom: '8px', 
  fontWeight: '700', 
  fontSize: '14px', 
  color: '#0f172a' 
};

const inputStyle = { 
  width: '100%', 
  padding: '14px 16px', 
  borderRadius: '12px', 
  border: '1px solid #cbd5e1', 
  backgroundColor: 'white', 
  fontSize: '15px', 
  color: '#0f172a', 
  boxSizing: 'border-box',
  outline: 'none',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
};

// Simple focus handlers for aesthetic rings
const handleFocus = (e) => {
  e.target.style.borderColor = '#4f46e5';
  e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
};

const handleBlur = (e) => {
  e.target.style.borderColor = '#cbd5e1';
  e.target.style.boxShadow = 'none';
};